/**
 * Partners Redux Slice
 * 
 * Clean state management for partner configurations list with filtering and pagination.
 * Follows single responsibility - only manages partner list state.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import type {
  PartnerResponse,
  PartnerQueryParams,
  PartnerListResponse,
} from '@/app/types/partners';
import { partnerService } from '@/app/services/partnerService';

// ============================================================================
// Types
// ============================================================================

interface PartnersState {
  items: PartnerResponse[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: PartnerQueryParams;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  invalidated: boolean;
}

// ============================================================================
// Constants
// ============================================================================

// Page size options available to users
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PageSizeOption = typeof PAGE_SIZE_OPTIONS[number];

// Default page size
const DEFAULT_PAGE_SIZE: PageSizeOption = 10;

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: PartnerQueryParams = {
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

const initialState: PartnersState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: initialFilters,
  status: 'idle',
  error: null,
  invalidated: false,
};

// ============================================================================
// Async Thunks
// ============================================================================

export const fetchPartners = createAsyncThunk<
  PartnerListResponse,
  PartnerQueryParams | undefined,
  { state: RootState; rejectValue: string }
>(
  'partners/fetch',
  async (params, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const filters = params ?? state.partners.filters;
      return await partnerService.queryPartners(filters);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch partners'
      );
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const partnersSlice = createSlice({
  name: 'partners',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<PartnerQueryParams>>) => {
      // Merge new filters with existing, reset to page 1
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0, // Reset pagination when filters change
      };
      state.currentPage = 1;
    },
    
    setPage: (state, action: PayloadAction<number>) => {
      const page = action.payload;
      state.currentPage = page;
      state.filters.offset = (page - 1) * state.pageSize;
    },
    
    setPageSize: (state, action: PayloadAction<PageSizeOption>) => {
      state.pageSize = action.payload;
      state.filters.limit = action.payload;
      state.filters.offset = 0; // Reset to first page when changing page size
      state.currentPage = 1;
    },
    
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.currentPage = 1;
    },
    
    invalidate: (state) => {
      state.invalidated = true;
    },
    
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPartners.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPartners.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.partners;
        state.totalCount = action.payload.total;
        state.invalidated = false;
      })
      .addCase(fetchPartners.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

// ============================================================================
// Actions & Selectors
// ============================================================================

export const { setFilters, setPage, setPageSize, clearFilters, invalidate, resetState } = partnersSlice.actions;

// Selectors
export const selectPartners = (state: RootState) => state.partners.items;
export const selectPartnersTotalCount = (state: RootState) => state.partners.totalCount;
export const selectPartnersCurrentPage = (state: RootState) => state.partners.currentPage;
export const selectPartnersPageSize = (state: RootState) => state.partners.pageSize;
export const selectPartnersFilters = (state: RootState) => state.partners.filters;
export const selectPartnersStatus = (state: RootState) => state.partners.status;
export const selectPartnersError = (state: RootState) => state.partners.error;

// Derived selectors
export const selectPartnersIsLoading = (state: RootState) => 
  state.partners.status === 'loading';

export const selectPartnersInvalidated = (state: RootState) => 
  state.partners.invalidated;

export const selectPartnersTotalPages = (state: RootState) => 
  Math.ceil(state.partners.totalCount / state.partners.pageSize);

export default partnersSlice.reducer;

